import React, { useEffect, useMemo, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { ClipboardList, Plus, Search, MoreVertical } from "lucide-react";
import apiClient from "../../utils/apiClient";
import DataTable from "../Common/DataTable";
import {
  PageHeader, ConfirmModal, AppButton,
  FilterButton, ActiveFilterChips, FilterPanel, FilterSelectField, useListFilters,
} from "../ui";
import { useNavigate } from "react-router-dom";
import { getCurrentUser } from "../../utils/auth";
import { formatToReadableDateTime } from "../../utils/common";
import "./Assignments.scss";

const STATUSES = ["", "draft", "published", "closed"];

const statusClassMap = {
  draft: "asl-status--draft",
  published: "asl-status--published",
  closed: "asl-status--closed",
};

const AssignmentList = () => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [batches, setBatches] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const navigate = useNavigate();
  const prefix = getCurrentUser()?.usertype === "instructor" ? "/i" : "";

  /* The FilterPanel pattern (D3), replacing an inline `d-none d-md-block` strip
     PLUS a `d-md-none` button-and-modal — two implementations of the same two
     filters. `empty: ""` because this API reads the empty string, not "all". */
  const listFilters = useListFilters({
    status: {
      label: "Status", empty: "",
      options: [
        { value: "", label: "All Statuses" },
        ...STATUSES.filter(Boolean).map((st) => ({ value: st, label: st.charAt(0).toUpperCase() + st.slice(1) })),
      ],
    },
    batch: {
      label: "Batch", empty: "",
      options: [{ value: "", label: "All Batches" }, ...batches.map((b) => ({ value: b._id, label: b.name }))],
    },
  });
  /* ⚠️ Memoised because it is a `useEffect` dependency below. As a bare object
     literal it would be a fresh identity every render and the fetch effect would
     loop. It was `useState` before, which gave that for free. */
  const filterValues = listFilters.values;
  const filters = useMemo(
    () => ({ ...filterValues, search }),
    [filterValues, search],
  );

  // Fetch batches for filter dropdown
  useEffect(() => {
    apiClient.get("/batches/list").then(res => {
      setBatches(res.data.batches || res.data || []);
    }).catch(() => setBatches([]));
  }, []);

  // Fetch paginated assignments
  useEffect(() => {
    const fetchAssignments = async () => {
      setLoading(true);
      try {
        const { data } = await apiClient.get("/assignments/list", {
          params: {
            ...filters,
            page,
            limit: pageSize,
          },
        });
        setAssignments(data.assignments || []);
        setTotalRecords(data.total || 0);
      } catch {
        setAssignments([]);
        setTotalRecords(0);
      }
      setLoading(false);
    };
    fetchAssignments();
  }, [filters, page, pageSize, refreshKey]);

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [filters]);

  const [deleteAssignmentId, setDeleteAssignmentId] = useState(null);

  const handleDelete = (id) => setDeleteAssignmentId(id);

  const confirmDelete = async () => {
    const id = deleteAssignmentId;
    setDeleteAssignmentId(null);
    await apiClient.delete(`/assignments/${id}`);
    setRefreshKey((k) => k + 1);
  };

  const handlePublish = async (id) => {
    await apiClient.post(`/assignments/${id}/publish`);
    setRefreshKey((k) => k + 1);
  };

  const handleClose = async (id) => {
    await apiClient.post(`/assignments/${id}/close`);
    setRefreshKey((k) => k + 1);
  };

  // Commits on blur / Enter, not per keystroke — which is why this page keeps
  // its own input rather than taking ui/SearchInput.
  const handleSearchBlur = () => {
    if (searchInput !== search) setSearch(searchInput);
  };


  const columns = [
    {
      key: "index",
      label: "#",
      width: "50px",
      render: (_, index) => (page - 1) * pageSize + index + 1,
    },
    {
      key: "title",
      label: "Title",
      width: "25%",
      render: (a) => (
        <div className="fw-medium" title={a.title}>
          {a.title?.length > 60 ? a.title.substring(0, 60) + "..." : a.title}
        </div>
      ),
    },
    {
      key: "batch",
      label: "Batch",
      width: "15%",
      render: (a) => a.batch?.name || "-",
    },
    {
      key: "status",
      label: "Status",
      width: "10%",
      render: (a) => (
        <span className={`asl-status ${statusClassMap[a.status] || ""}`}>
          {a.status ? a.status.charAt(0).toUpperCase() + a.status.slice(1) : "-"}
        </span>
      ),
    },
    {
      key: "startDate",
      label: "Start",
      width: "15%",
      render: (a) => (a.startDate ? formatToReadableDateTime(a.startDate) : "-"),
    },
    {
      key: "endDate",
      label: "End",
      width: "15%",
      render: (a) => (a.endDate ? formatToReadableDateTime(a.endDate) : "-"),
    },
    {
      key: "questions",
      label: "Questions",
      width: "8%",
      render: (a) => a.questions?.length || 0,
    },
    {
      key: "actions",
      label: "",
      width: "50px",
      render: (a) => (
        <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
          <Dropdown align="end">
            <Dropdown.Toggle
              variant="light"
              bsPrefix="p-0 border-0 bg-transparent shadow-none"
              className="asl-action-btn"
            >
              <MoreVertical size={16} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() => navigate(`${prefix}/assignments/${a._id}`)}
                disabled={a.status === "closed"}
              >
                View
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => navigate(`${prefix}/assignments/edit/${a._id}`)}
                disabled={a.status === "closed"}
              >
                Edit
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => handlePublish(a._id)}
                disabled={a.status !== "draft"}
              >
                Publish
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => handleClose(a._id)}
                disabled={a.status !== "published"}
              >
                Close
              </Dropdown.Item>
              <Dropdown.Item
                className="text-danger"
                onClick={() => handleDelete(a._id)}
                disabled={a.status === "closed"}
              >
                Delete
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
  <>
    <div className="asl-page">
      <PageHeader
        icon={ClipboardList}
        title="Assignments"
        description="Create and manage coding assignments for your batches"
      />

      {/* Compact Toolbar */}
      <div className="asl-toolbar">
        <AppButton
          size="sm"
          className="asl-add-btn"
          onClick={() => navigate(`${prefix}/create-assignment`)}
        >
          <Plus size={16} />
          <span>Add Assignment</span>
        </AppButton>

        <div className="asl-search">
          <Search size={16} className="asl-search-icon" />
          <input
            type="text"
            className="asl-search-input"
            placeholder="Search assignments..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onBlur={handleSearchBlur}
            onKeyDown={(e) => e.key === "Enter" && handleSearchBlur()}
          />
        </div>

        <div className="asl-toolbar-actions">
          <FilterButton count={listFilters.count} onClick={listFilters.open} />
        </div>
      </div>

      <ActiveFilterChips items={listFilters.activeFilters} onClearAll={listFilters.reset} />

      <FilterPanel {...listFilters.panelProps} title="Filter Assignments">
        <FilterSelectField {...listFilters.selectProps("status")} />
        <FilterSelectField {...listFilters.selectProps("batch")} />
      </FilterPanel>

      <DataTable
        data={assignments}
        columns={columns}
        loading={loading}
        hoverable={true}
        pagination={{
          page,
          pageSize,
          total: totalRecords,
          onPageChange: setPage,
        }}
        emptyMessage="No assignments found"
      />
    </div>

    <ConfirmModal
      show={!!deleteAssignmentId}
      onHide={() => setDeleteAssignmentId(null)}
      onConfirm={confirmDelete}
      title="Delete Assignment"
      message="Delete this assignment?"
      confirmText="Delete"
      variant="danger"
    />
  </>
  );
};

export default AssignmentList;
